import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { useRowEditing } from "../../hooks/useRowEditing";
import CommandeDetailModal from "../../components/CommandeDetailModal";
import { useOptimisticProductionRows } from "../production/useOptimisticProductionRows";
import { productionSaveStatus } from "../production/SaveStatusBadge";
import { StatutMedicamentLabels } from "../../models/statutMedicament.map";
import SendToEhpadModal from "../../components/SendToEhpadModal";
import {
    CLIENT_CLASSIFICATION_FILTERS,
    FilterField,
    PRODUCTION_PAGE_SIZE,
    PatientInfo,
    PrinterIcon,
    ProductSummary,
    RefreshIcon,
    STATUTS_HORS_PRODUCTION,
    StampCheck,
    matchesClassificationFilters,
    openEtiquettesPrintWindow,
    productTone,
} from "../production/ProductionTableUi";
import ProductionSearchBar from "../production/ProductionSearchBar";
import { useDebouncedValue } from "../production/productionSearch";

function ruptureMailContent(row, pharmacyName) {
    const dosage = row.dosage ? ` (${row.dosage})` : "";
    return `Bonjour,\n\nLe produit ${row.nom}${dosage} est en rupture, nous vous proposons de le remplacer par [...].\n\nVeuillez nous joindre une ordonnance avec la nouvelle prescription.\n\nBien à vous,\n${pharmacyName || "La Pharmacie"}`;
}

export default function RobotProduction({ headerTabs = null }) {
    const { user, selectedPharmacy, pharmacyId: currentPharmacyId } = useAuth();
    const { rows, setRows, applyServerRows, appendServerRows, patchRow, stageRow, flushRows } = useOptimisticProductionRows();
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const scrollRef = useRef(null);
    const loadSeqRef = useRef(0);
    const loadingNextPageRef = useRef(false);
    const sentinelRef = useRef(null);
    const flushPatientRows = useCallback((commandePatientId) => {
        const ids = rowsRef.current
            .filter(row => row.commandePatientId === commandePatientId)
            .map(row => row.id);
        return flushRows(ids);
    }, [flushRows]);
    const { activeId: activeRowId, editingMap, select: selectRow, releaseActive: releaseActiveRow } = useRowEditing(
        currentPharmacyId,
        user?.email,
        { onBeforeUnlock: flushPatientRows }
    );
    const activeRowIdRef = useRef(null);
    activeRowIdRef.current = activeRowId;
    const [loading, setLoading]     = useState(false);
    const [tournees, setTournees]         = useState([]);
    const [tourneeFilter, setTourneeFilter] = useState("");
    const [classificationGroupFilter, setClassificationGroupFilter] = useState("");
    const [searchText, setSearchText] = useState("");
    const searchQuery = useDebouncedValue(searchText).trim();
    const [modalCommandeId, setModalCommandeId] = useState(null);
    const [ruptureMailRow, setRuptureMailRow] = useState(null);
    const [currentPharmacy, setCurrentPharmacy] = useState(selectedPharmacy ?? null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingNextPage, setLoadingNextPage] = useState(false);
    const [printSelectionMode, setPrintSelectionMode] = useState(false);
    const [selectedPrintIds, setSelectedPrintIds] = useState(() => new Set());

    useEffect(() => {
        API.get("/ehpads/tournees").then(r => setTournees(r.data)).catch(() => {});
    }, []);

    const selectedPharmacyId = selectedPharmacy?.id ?? null;
    const selectedPharmacyName = selectedPharmacy?.name ?? null;

    useEffect(() => {
        const authPharmacy = selectedPharmacyId || selectedPharmacyName
            ? { id: selectedPharmacyId, name: selectedPharmacyName }
            : null;
        setCurrentPharmacy(authPharmacy);
        API.get("/admin/pharmacy", { noRedirectOn401: true })
            .then(r => setCurrentPharmacy(r.data ?? authPharmacy))
            .catch(() => setCurrentPharmacy(authPharmacy));
    }, [currentPharmacyId, selectedPharmacyId, selectedPharmacyName]);

    useEffect(() => {
        setRows(prev => prev.map(r => {
            const editing = editingMap[r.commandePatientId];
            return editing !== undefined ? { ...r, rowEditingBy: editing ?? null } : r;
        }));
    }, [editingMap, setRows]);

    const loadProductionPage = useCallback((pageToLoad = 0, reset = false) => {
        const seq = reset ? ++loadSeqRef.current : loadSeqRef.current;
        if (reset) setLoading(true);
        else {
            loadingNextPageRef.current = true;
            setLoadingNextPage(true);
        }
        const params = new URLSearchParams({ destinationType: "ROBOT" });
        if (tourneeFilter) params.append("tournee", tourneeFilter);
        if (classificationGroupFilter) params.append("classificationPatientGroup", classificationGroupFilter);
        if (searchQuery) params.append("q", searchQuery);
        params.append("page", String(pageToLoad));
        params.append("size", String(PRODUCTION_PAGE_SIZE));
        API.get(`/medicaments/production?${params}`, { noRedirectOn401: true })
            .then(r => {
                if (seq !== loadSeqRef.current) return;
                const payload = r.data || {};
                const serverRows = Array.isArray(payload) ? payload : (payload.content || []);
                const visibleRows = serverRows.filter(row => !STATUTS_HORS_PRODUCTION.has(row.statut));
                if (reset) applyServerRows(visibleRows);
                else appendServerRows(visibleRows);
                setPage(pageToLoad);
                setHasMore(Array.isArray(payload) ? false : !payload.last);
            })
            .catch(console.error)
            .finally(() => {
                if (seq !== loadSeqRef.current) return;
                if (reset) setLoading(false);
                else {
                    loadingNextPageRef.current = false;
                    setLoadingNextPage(false);
                }
            });
    }, [appendServerRows, applyServerRows, classificationGroupFilter, searchQuery, tourneeFilter]);

    const refresh = useCallback(async () => {
        if (activeRowIdRef.current) {
            const saved = await flushPatientRows(activeRowIdRef.current);
            if (!saved) return;
        }
        setHasMore(false);
        setPage(0);
        loadingNextPageRef.current = false;
        loadProductionPage(0, true);
    }, [flushPatientRows, loadProductionPage]);

    const loadNextPage = useCallback(() => {
        if (loading || loadingNextPage || loadingNextPageRef.current || !hasMore) return;
        loadProductionPage(page + 1, false);
    }, [hasMore, loadProductionPage, loading, loadingNextPage, page]);

    const handleScroll = useCallback((event) => {
        const target = event.currentTarget;
        if (target.scrollHeight - target.scrollTop - target.clientHeight < 240) {
            loadNextPage();
        }
    }, [loadNextPage]);

    useEffect(() => {
        if (!hasMore || typeof window === "undefined") return undefined;
        const handleAnyScroll = () => {
            const sentinel = sentinelRef.current;
            if (!sentinel) return;
            if (sentinel.getBoundingClientRect().top <= window.innerHeight + 240) {
                loadNextPage();
            }
        };
        window.addEventListener("scroll", handleAnyScroll, true);
        return () => window.removeEventListener("scroll", handleAnyScroll, true);
    }, [hasMore, loadNextPage]);

    useEffect(() => {
        if (!hasMore || typeof IntersectionObserver === "undefined") return undefined;
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) loadNextPage();
        }, { root: scrollRef.current, rootMargin: "240px" });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadNextPage]);

    useEffect(() => { void refresh(); }, [refresh]);

    const patch = stageRow;
    const currentOperator = user?.username || user?.email || null;

    useEffect(() => {
        setSelectedPrintIds(current => {
            const availableIds = new Set(rows.map(row => row.id));
            const next = new Set([...current].filter(id => availableIds.has(id)));
            return next.size === current.size ? current : next;
        });
    }, [rows]);

    const selectedPrintRows = useMemo(
        () => rows.filter(row => selectedPrintIds.has(row.id)),
        [rows, selectedPrintIds]
    );

    const handlePrintClick = () => {
        if (!printSelectionMode) {
            setPrintSelectionMode(true);
            return;
        }
        if (selectedPrintRows.length === 0) return;
        const printPharmacy = currentPharmacy ?? selectedPharmacy;
        if (!printPharmacy?.name) return;
        openEtiquettesPrintWindow(selectedPrintRows, printPharmacy, "Étiquettes Robot");
    };

    const cancelPrintSelection = () => {
        setPrintSelectionMode(false);
        setSelectedPrintIds(new Set());
    };

    const togglePrintSelection = id => {
        setSelectedPrintIds(current => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBilling = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { billingValidated: checked, billingUsername: by },
            { billingValidated: checked, billingUsername: by, billingHour: now }
        );
    };

    const handlePlanification = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { planificationValidated: checked, planificationValidatedUsername: by },
            { planificationValidated: checked, planificationValidatedAt: now, planificationValidatedBy: by }
        );
    };

    const handleProduction = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { productionValidated: checked, productionValidatedUsername: by },
            { productionValidated: checked, caisseHour: now, caisseUsername: by, statut: checked ? "TRAITE" : null }
        );
    };

    const handleProductionStarted = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { productionStarted: checked, productionStartedUsername: by },
            { productionStarted: checked, productionStartedAt: now, productionStartedBy: by }
        );
    };

    const handleRupture = async (row, enRupture) => {
        await flushRows([row.id]);
        const requestFields = enRupture
            ? {
                statut: "EN_RUPTURE",
                planificationValidated: false,
                productionValidated: false,
                ehpadWarnedIfOutOfStock: false,
            }
            : {
                statut: "",
                planificationValidated: false,
                productionValidated: false,
                ehpadWarnedIfOutOfStock: false,
            };
        const optimisticFields = enRupture
            ? {
                statut: "EN_RUPTURE",
                planificationValidated: false,
                planificationValidatedAt: null,
                planificationValidatedBy: null,
                productionValidated: false,
                caisseHour: null,
                caisseUsername: null,
                ehpadWarnedIfOutOfStock: false,
            }
            : {
                statut: null,
                planificationValidated: false,
                planificationValidatedAt: null,
                planificationValidatedBy: null,
                productionValidated: false,
                caisseHour: null,
                caisseUsername: null,
                ehpadWarnedIfOutOfStock: false,
            };
        await patchRow(row.id, requestFields, optimisticFields);
    };

    const handleEhpadWarned = async (id, checked) => {
        await patchRow(id, { ehpadWarnedIfOutOfStock: checked });
    };

    const grouped = useMemo(() => {
        const map = new Map();
        rows.forEach(row => {
            const patientKey = row.commandePatientId
                ?? row.patientId
                ?? `${row.patientNom || ""}:${row.patientPrenom || ""}`;
            const groupKey = `${row.commandeId}:${patientKey}`;
            if (!map.has(groupKey)) map.set(groupKey, []);
            map.get(groupKey).push(row);
        });
        return [...map.values()];
    }, [rows]);

    const filtered = useMemo(() => {
        let result = grouped;
        if (tourneeFilter) {
            result = result.filter(g => g[0].ehpadTournee === tourneeFilter);
        }
        if (classificationGroupFilter) {
            result = result.filter(g => matchesClassificationFilters(g[0].classification, classificationGroupFilter, ""));
        }
        return result;
    }, [grouped, tourneeFilter, classificationGroupFilter]);

    const total = rows.length;
    const visibleCount = filtered.reduce((acc, g) => acc + g.length, 0);
    const selectedPrintCount = selectedPrintRows.length;

    return (
        <div className="rbt-page">
            <header className="rbt-header">
                <div className="rbt-header__title">
                    <h2>Robot</h2>
                    <span className="rbt-count" title={visibleCount === total ? `${total} produit(s)` : `${visibleCount} produit(s) affiché(s) sur ${total}`}>
                        {visibleCount}{visibleCount === total ? "" : ` / ${total}`}
                    </span>
                </div>
                {headerTabs}
                <div className="rbt-header__spacer" />
                {printSelectionMode && (
                    <span className={`rbt-selection-count${selectedPrintCount > 0 ? " is-active" : ""}`}>
                        {selectedPrintCount} sélectionné(s)
                    </span>
                )}
                <button
                    type="button"
                    className={`rbt-btn ${printSelectionMode ? "rbt-btn--primary" : "rbt-btn--label"}`}
                    onClick={handlePrintClick}
                    aria-disabled={printSelectionMode && selectedPrintCount === 0}
                    title={printSelectionMode && selectedPrintCount === 0 ? "Sélectionnez au moins un médicament" : "Imprimer étiquette"}
                >
                    <PrinterIcon />
                    <span>Imprimer étiquette</span>
                </button>
                {printSelectionMode && (
                    <button type="button" className="rbt-btn" onClick={cancelPrintSelection}>
                        Annuler
                    </button>
                )}
                <button type="button" className="rbt-btn" onClick={refresh}>
                    <RefreshIcon />
                    Actualiser
                </button>
            </header>

            <div className="rbt-filters">
                <FilterField label="Tournée" active={!!tourneeFilter}>
                    <select value={tourneeFilter} onChange={e => setTourneeFilter(e.target.value)}>
                        <option value="">Toutes</option>
                        {tournees.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                </FilterField>
                <FilterField label="Type client" active={!!classificationGroupFilter}>
                    <select value={classificationGroupFilter} onChange={e => setClassificationGroupFilter(e.target.value)}>
                        {CLIENT_CLASSIFICATION_FILTERS.map(option => (
                            <option key={option.value || "ALL"} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FilterField>
                <ProductionSearchBar value={searchText} onChange={setSearchText} />
            </div>

            <div ref={scrollRef} onScroll={handleScroll} className="rbt-table-card">
                {loading && <div className="rbt-empty">Chargement…</div>}
                {!loading && rows.length === 0 && <div className="rbt-empty">Aucun médicament.</div>}
                {!loading && rows.length > 0 && (
                    <>
                    <table className={`rbt-table rbt-table--robot${printSelectionMode ? " is-print-mode" : ""}`}>
                        <colgroup>
                            {printSelectionMode && <col className="rbt-col--select" />}
                            <col className="rbt-col--info" />
                            <col className="rbt-col--product" />
                            <col className="rbt-col--qty" />
                            <col className="rbt-col--check" />
                            <col className="rbt-col--check" />
                            <col className="rbt-col--comment" />
                            <col className="rbt-col--started" />
                            <col className="rbt-col--group-action" />
                            <col className="rbt-col--production-check" />
                        </colgroup>
                        <thead>
                            <tr>
                                {printSelectionMode && <th className="rbt-th rbt-th--center rbt-th--select">Sélect.</th>}
                                <th className="rbt-th rbt-th--info"><span className="rbt-th-label-line">Informations</span></th>
                                <th className="rbt-th"><span className="rbt-th-label-line">Produit</span></th>
                                <th className="rbt-th rbt-th--center rbt-th--qty"><span className="rbt-th-label-line">Qté</span></th>
                                <th className="rbt-th rbt-th--center rbt-th--check rbt-th--compact-check">
                                    <span className="rbt-th-label-line">Valid.</span>
                                    <span className="rbt-th-label-line">facturation</span>
                                    <span className="rbt-th-label-line">(LGPI)</span>
                                </th>
                                <th className="rbt-th rbt-th--center rbt-th--check rbt-th--compact-check">
                                    <span className="rbt-th-label-line">Validation</span>
                                    <span className="rbt-th-label-line">planification</span>
                                </th>
                                <th className="rbt-th rbt-th--comment"><span className="rbt-th-label-line">Commentaire</span></th>
                                <th className="rbt-th rbt-th--center rbt-th--started rbt-th--compact-check">
                                    <span className="rbt-th-label-line">Production</span>
                                    <span className="rbt-th-label-line">envoyé</span>
                                    <span className="rbt-th-label-line">au robot</span>
                                </th>
                                <th className="rbt-th rbt-th--production" colSpan={2}><span className="rbt-th-label-line">Production réalisée</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(group => {
                                const allBilling = group.every(r => !!r.billingValidated);
                                const allPlanif  = group.every(r => !!r.planificationValidated);
                                const allProductionStarted = group.every(r => !!r.productionStarted);
                                const allProduction = group.every(r => !!r.productionValidated);
                                const billingRef = group.find(r => r.billingHour) || group[0];
                                const planifRef  = group.find(r => r.planificationValidatedAt) || group[0];
                                const productionStartedRef = group.find(r => r.productionStartedAt) || group[0];
                                const groupComment = group[0].commentaryPlanificationRobot || group[0].commentaryProductionRobot || "";
                                const saveStatus   = productionSaveStatus(group);
                                const cpId        = group[0].commandePatientId;
                                const rowEditedBy = group[0].rowEditingBy || null;
                                const isActiveRow = activeRowId === cpId;
                                const isLockedByCurrentUser = rowEditedBy === user?.email;
                                const groupPrintSelected = group.some(r => selectedPrintIds.has(r.id));
                                return group.map((row, idx) => (
                                    <RobotRow
                                        key={row.id}
                                        row={row}
                                        isFirst={idx === 0}
                                        isLast={idx === group.length - 1}
                                        groupSize={group.length}
                                        groupBillingValidated={allBilling}
                                        groupBillingRef={billingRef}
                                        groupPlanifValidated={allPlanif}
                                        groupPlanifRef={planifRef}
                                        groupProductionStarted={allProductionStarted}
                                        groupProductionStartedRef={productionStartedRef}
                                        groupProductionValidated={allProduction}
                                        saveStatus={saveStatus}
                                        groupComment={groupComment}
                                        onGroupBilling={checked => group.forEach(r => handleBilling(r.id, checked))}
                                        onGroupPlanif={checked => group.forEach(r => handlePlanification(r.id, checked))}
                                        onGroupProductionStarted={checked => group.filter(r => r.statut !== "EN_RUPTURE").forEach(r => handleProductionStarted(r.id, checked))}
                                        onGroupProduction={checked => group.filter(r => r.statut !== "EN_RUPTURE").forEach(r => handleProduction(r.id, checked))}
                                        onProduction={checked => handleProduction(row.id, checked)}
                                        onRupture={enRupture => handleRupture(row, enRupture)}
                                        onEhpadWarned={checked => handleEhpadWarned(row.id, checked)}
                                        onRuptureMail={setRuptureMailRow}
                                        onGroupComment={text => group.forEach(r => patch(r.id, { commentaryPlanificationRobot: text }))}
                                        onOpenMail={setModalCommandeId}
                                        rowEditedBy={rowEditedBy}
                                        isActiveRow={isActiveRow}
                                        isLockedByCurrentUser={isLockedByCurrentUser}
                                        onGroupSelect={() => selectRow(cpId)}
                                        printSelectionMode={printSelectionMode}
                                        selectedForPrint={selectedPrintIds.has(row.id)}
                                        groupPrintSelected={groupPrintSelected}
                                        onTogglePrintSelection={() => togglePrintSelection(row.id)}
                                    />
                                ));
                            })}
                        </tbody>
                    </table>
                    <div ref={sentinelRef} className="rbt-sentinel">
                        {loadingNextPage && "Chargement des patients suivants…"}
                        {!loadingNextPage && (hasMore ? "Faites défiler pour charger la suite." : "Tous les patients chargés.")}
                    </div>
                    </>
                )}
            </div>

            {modalCommandeId && (
                <CommandeDetailModal
                    commandeId={modalCommandeId}
                    onClose={() => setModalCommandeId(null)}
                    onBeforeOpenValidation={releaseActiveRow}
                />
            )}

            {ruptureMailRow && (
                <SendToEhpadModal
                    commande={{
                        id: ruptureMailRow.commandeId,
                        ehpadNom: ruptureMailRow.ehpadNom,
                        ehpad: { emailsContact: ruptureMailRow.ehpadEmailsContact || [] }
                    }}
                    initialSujet={`Rupture de stock — ${ruptureMailRow.nom}`}
                    initialContenu={ruptureMailContent(ruptureMailRow, selectedPharmacy?.name)}
                    onClose={(sent) => {
                        if (sent) handleEhpadWarned(ruptureMailRow.id, true);
                        setRuptureMailRow(null);
                    }}
                />
            )}
        </div>
    );
}

function RobotRow({
    row, isFirst, isLast, groupSize,
    groupBillingValidated, groupBillingRef,
    groupPlanifValidated, groupPlanifRef,
    groupProductionStarted, groupProductionStartedRef,
    groupProductionValidated,
    saveStatus,
    groupComment, onGroupComment,
    onGroupBilling, onGroupPlanif, onGroupProductionStarted, onGroupProduction, onProduction, onRupture, onEhpadWarned, onRuptureMail,
    onOpenMail,
    rowEditedBy = null, isActiveRow = false, isLockedByCurrentUser = false, onGroupSelect,
    printSelectionMode = false, selectedForPrint = false, groupPrintSelected = false, onTogglePrintSelection,
}) {
    const [comment, setComment] = useState(groupComment ?? "");
    const [commentDirty, setCommentDirty] = useState(false);
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const done = !!row.productionValidated;
    const enRupture = row.statut === "EN_RUPTURE";
    const isLockedByOther = !!rowEditedBy && !isActiveRow && !isLockedByCurrentUser;
    const tone = productTone({ enRupture, done, planned: groupPlanifValidated });
    const rowEndClass = isLast ? " is-group-end" : "";

    useEffect(() => {
        if (!commentDirty) setComment(groupComment ?? "");
    }, [commentDirty, groupComment]);

    useEffect(() => {
        if (!contextMenuOpen) return undefined;
        const close = () => setContextMenuOpen(false);
        window.addEventListener("click", close);
        window.addEventListener("contextmenu", close);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("contextmenu", close);
        };
    }, [contextMenuOpen]);

    // Coche immédiatement, puis prend le verrou de la ligne ; annule la coche si le verrou est refusé.
    const toggleWithLock = (apply) => async (event) => {
        const checked = event.target.checked;
        apply(checked);
        if (!isActiveRow) {
            const ok = await onGroupSelect();
            if (!ok) apply(!checked);
        }
    };

    const handleRuptureAction = async () => {
        if (!isActiveRow) {
            const ok = await onGroupSelect();
            if (!ok) return;
        }
        onRupture(!enRupture);
    };

    const rowClassName = [
        "rbt-row",
        printSelectionMode ? "is-print-mode" : "",
        groupPrintSelected ? "is-print-selected" : "",
    ].filter(Boolean).join(" ");
    let infoStateClass = "";
    if (isActiveRow) infoStateClass = " is-active";
    else if (isLockedByOther) infoStateClass = " is-locked";

    return (
        <tr
            className={rowClassName}
            onClickCapture={event => {
                if (!printSelectionMode) return;
                event.preventDefault();
                event.stopPropagation();
                onTogglePrintSelection();
            }}
            title={printSelectionMode ? "Cliquer pour sélectionner cette étiquette" : undefined}
        >
            {printSelectionMode && (
                <td className={`rbt-select-cell rbt-center${rowEndClass}`}>
                    <input
                        type="checkbox"
                        className="rbt-checkbox"
                        aria-label={`Sélectionner l'étiquette ${row.nom || "produit"}`}
                        checked={selectedForPrint}
                        readOnly
                    />
                </td>
            )}

            {/* ── Informations (fusionnées par patient) ── */}
            {isFirst && (
                <td
                    rowSpan={groupSize}
                    onClick={onGroupSelect}
                    className={`rbt-info-cell rbt-merged${infoStateClass}`}
                    title={isLockedByOther ? `En cours d'édition par ${rowEditedBy}` : "Cliquer pour indiquer que vous éditez ce patient"}
                >
                    <PatientInfo
                        row={row}
                        saveStatus={saveStatus}
                        rowEditedBy={rowEditedBy}
                        isActiveRow={isActiveRow}
                        isLockedByOther={isLockedByOther}
                        onOpenMail={onOpenMail}
                    />
                </td>
            )}

            {/* ── Produit ── */}
            <td
                onContextMenu={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!enRupture && !isLockedByOther) setContextMenuOpen(true);
                }}
                className={`rbt-product-cell rbt-tone--${tone}${rowEndClass}`}
            >
                <ProductSummary row={row} printSelected={selectedForPrint} />
                {enRupture && (
                    <button
                        type="button"
                        className="rbt-badge-action"
                        onClick={handleRuptureAction}
                        disabled={isLockedByOther}
                    >
                        Annuler rupture
                    </button>
                )}
                {contextMenuOpen && (
                    <div role="menu" className="rbt-context-menu">
                        <button
                            type="button"
                            role="menuitem"
                            onClick={async event => {
                                event.stopPropagation();
                                setContextMenuOpen(false);
                                await handleRuptureAction();
                            }}
                        >
                            Marquer en rupture
                        </button>
                    </div>
                )}
            </td>

            {/* ── Quantité ── */}
            <td className={`rbt-qty-cell rbt-tone--${tone}${rowEndClass}`}>×{row.quantite ?? "—"}</td>

            {/* ── Validation facturation LGPI (fusionnée par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged rbt-center">
                    <StampCheck
                        label="Validation facturation"
                        checked={groupBillingValidated}
                        at={groupBillingRef?.billingHour}
                        by={groupBillingRef?.billingUsername}
                        disabled={isLockedByOther}
                        onChange={toggleWithLock(onGroupBilling)}
                    />
                </td>
            )}

            {/* ── Validation planification (fusionnée par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged rbt-center">
                    <StampCheck
                        label="Validation planification"
                        checked={groupPlanifValidated}
                        at={groupPlanifRef?.planificationValidatedAt}
                        by={groupPlanifRef?.planificationValidatedBy}
                        disabled={isLockedByOther}
                        onChange={toggleWithLock(onGroupPlanif)}
                    />
                </td>
            )}

            {/* ── Commentaire (fusionné par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged">
                    <textarea
                        className={`rbt-comment${comment ? " has-value" : ""}`}
                        rows={3}
                        cols={10}
                        value={comment}
                        aria-label="Commentaire de planification robot"
                        onFocus={async () => { if (!isActiveRow) await onGroupSelect(); }}
                        onChange={e => {
                            setCommentDirty(true);
                            setComment(e.target.value);
                        }}
                        onBlur={() => {
                            if (commentDirty && isActiveRow) onGroupComment(comment);
                            setCommentDirty(false);
                        }}
                        disabled={isLockedByOther}
                        placeholder="Ajouter un commentaire…"
                    />
                </td>
            )}

            {/* ── Production envoyée au robot (fusionnée par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged rbt-center">
                    <StampCheck
                        label="Production envoyé au robot"
                        checked={groupProductionStarted}
                        at={groupProductionStartedRef?.productionStartedAt}
                        by={groupProductionStartedRef?.productionStartedBy}
                        disabled={isLockedByOther || enRupture}
                        onChange={toggleWithLock(onGroupProductionStarted)}
                    />
                </td>
            )}

            {/* ── Validation de tous les produits du patient ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged rbt-center rbt-group-action">
                    {groupSize > 1 && (
                        <button
                            type="button"
                            className={`rbt-btn-mini rbt-btn-mini--stacked${groupProductionValidated ? " is-done" : ""}`}
                            onClick={async () => {
                                const next = !groupProductionValidated;
                                onGroupProduction(next);
                                if (!isActiveRow) {
                                    const ok = await onGroupSelect();
                                    if (!ok) onGroupProduction(!next);
                                }
                            }}
                            disabled={isLockedByOther}
                            title={groupProductionValidated ? "Dévalider tous les produits du patient" : "Valider tous les produits du patient"}
                        >
                            {groupProductionValidated ? "Tout dévalider" : "Tout valider"}
                        </button>
                    )}
                </td>
            )}

            {/* ── Production réalisée (par produit) ── */}
            <td className={`rbt-production-cell${rowEndClass}`}>
                <div className="rbt-production">
                    {enRupture && <span className="rbt-rupture-status">{StatutMedicamentLabels.EN_RUPTURE}</span>}
                    {enRupture && (
                        <div className="rbt-rupture-actions">
                            <input
                                type="checkbox"
                                className="rbt-checkbox"
                                checked={!!row.ehpadWarnedIfOutOfStock}
                                disabled={isLockedByOther}
                                onChange={toggleWithLock(onEhpadWarned)}
                                title="EHPAD prévenue si rupture"
                                aria-label="EHPAD prévenue de la rupture"
                            />
                            {row.ehpadWarnedIfOutOfStock && <span className="rbt-warned">Prévenue</span>}
                            <button
                                type="button"
                                className="rbt-btn-mini rbt-btn-mini--alert"
                                onClick={() => onRuptureMail(row)}
                                disabled={isLockedByOther}
                                title="Envoyer un mail de rupture à l'EHPAD"
                            >
                                Prévenir
                            </button>
                        </div>
                    )}
                    <StampCheck
                        label={`Validation production ${row.nom || "produit"}`}
                        checked={done}
                        at={row.caisseHour}
                        by={row.caisseUsername}
                        disabled={isLockedByOther || enRupture}
                        onChange={toggleWithLock(onProduction)}
                    />
                </div>
            </td>
        </tr>
    );
}
